<template>
  <div
    class="h-full min-h-screen md:min-h-0 flex flex-col bg-black search-tracks"
  >
    <div class="bg-gray-600 text-gray-300 px-8 pt-12">
      <div class="mb-10 flex">
        <InputTextSearch v-model="searchQuery" />
        <div class="cursor-pointer flex ml-6" @click="setIsFooterOpen(true)">
          <Icon id="menu" color="#fff" />
        </div>
      </div>
      <Tabs :tabs="tabs" v-model="tabsIndexActive" />
    </div>
    <div class="flex-1 px-4 pb-56 md:overflow-y-scroll pt-10 music-list">
      <template v-if="tabsIndexActive === 0">
        <div
          :to="card.linkUrl"
          v-for="(card, index) in tracks"
          :key="index"
          @click="onTrack(card.id)"
        >
          <MusicCard
            :isDisabled="isLimitReached"
            :title="card.title"
            :subtitle="card.subtitle"
            :images="card.images"
            icon="dots"
            :numberSelection="trackIdsSelected.indexOf(card.id) + 1"
            :isSelected="trackIdsSelected.includes(card.id)"
            @onIcon="onTrackIcon(card.linkUrl)"
          />
        </div>
      </template>
      <template v-if="tabsIndexActive === 1">
        <router-link
          :to="card.linkUrl"
          v-for="(card, index) in albums"
          :key="index"
        >
          <MusicCard
            :title="card.title"
            :subtitle="card.subtitle"
            :images="card.images"
            icon="arrowRight"
          />
        </router-link>
      </template>
      <template v-if="tabsIndexActive === 2">
        <router-link
          :to="card.linkUrl"
          v-for="(card, index) in playlists"
          :key="index"
        >
          <MusicCard
            :title="card.title"
            :subtitle="card.subtitle"
            :images="card.images"
            icon="arrowRight"
          />
        </router-link>
      </template>
    </div>
    <div
      v-if="trackIdsSelected.length < campaignTrackNormalMin"
      class="
        py-7
        px-6
        fixed
        bg-black
        flex flex-col
        left-0
        md:absolute
        bottom-0
        w-full
      "
    >
      <TrackCount v-if="trackIdsSelected.length < campaignTrackNormalMin" />
    </div>
    <div
      v-if="trackIdsSelected.length >= campaignTrackNormalMin"
      class="px-10 py-12 fixed bg-black left-0 md:absolute bottom-0 w-full"
    >
      <router-link
        :to="campaignIsSetlist ? '/breaker/opening' : '/playlist-sort'"
      >
        <Button :title="$t('searchTracksAdd')" />
      </router-link>
      <div class="mt-4 text-xs text-white text-center" v-if="isLimitReached">
        {{ $t("searchTracksLimit") }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { TranslationHandler } from '../../mixins/translation-handler'
import { RestTrack, MusicCoverImage } from '../../types'
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { Getter, Mutation } from 'vuex-class'
import { uniqueBy } from '../../utils/array'

interface Item {
  id: number
  title: string
  subtitle: string
  images: MusicCoverImage[]
  linkUrl: string
}

@Component({
  components: {},
})
export default class SearchTracks extends Mixins(TranslationHandler) {
  @Getter campaignId!: number
  @Getter campaignTracks!: RestTrack[]
  @Getter campaignIsSetlist!: boolean
  @Getter campaignTrackNormalMin!: number
  @Getter campaignTrackNormalLimit!: number

  @Getter storageKeyTab!: string

  get tabs (): string[] {
    const tabs = [
      this.$t('searchTracksSongs'),
      this.$t('searchTracksAlbum'),
      this.$t('searchTracksPlaylists'),
    ]

    // no playlist tab if there are no playlists
    if (this.playlists.length === 0) {
      tabs.splice(2, 1)
    }

    return tabs
  }

  tabsIndexActive = 0

  @Watch('tabsIndexActive')
  onTabsIndexActiveChange (): void {
    localStorage.setItem(this.storageKeyTab, this.tabsIndexActive.toString())
  }

  mounted () {
    const tab = localStorage.getItem(this.storageKeyTab)

    if (tab) {
      this.tabsIndexActive = parseInt(tab, 10)
    }
  }

  searchQuery = ''

  get isLimitReached (): boolean {
    return this.campaignTrackNormalLimit === this.trackIdsSelected.length
  }

  get trackIdsSelected (): number[] {
    return this.$store.getters.campaignTrackIdsSelected
  }

  set trackIdsSelected (data: number[]) {
    this.$store.commit('SET_CAMPAIGN_TRACK_IDS_SELECTED', data)
  }

  get searchQueryLow (): string {
    return this.searchQuery.toLowerCase()
  }

  get tracks (): Item[] {
    return this.campaignTracks
      .map((track: RestTrack) => ({
        id: track.id,
        title: track.name,
        subtitle: track.artist_name,
        images: track.images,
        linkUrl: `/track/${track.id}`
      }))
      .filter((item) => {
        if (this.searchQuery === '' || this.tabsIndexActive !== 0) {
          return true
        } else {
          return item.title.toLowerCase().includes(this.searchQueryLow) ||
            item.subtitle.toLowerCase().includes(this.searchQueryLow)
        }
      })
      .filter((item) => !!item.title)
  }

  get albums (): Item[] {
    return uniqueBy(this.campaignTracks, 'album_name')
      .map((track: RestTrack) => ({
        id: -1,
        title: track.album_name,
        subtitle: track.artist_name,
        images: track.images,
        linkUrl: `/album/${encodeURIComponent(track.album_name)}`
      }))
      .filter((item) => {
        if (this.searchQuery === '' || this.tabsIndexActive !== 1) {
          return true
        } else {
          return item.title.toLowerCase().includes(this.searchQueryLow) ||
            item.subtitle.toLowerCase().includes(this.searchQueryLow)
        }
      })
      .filter((item) => !!item.title)
  }

  get playlists (): Item[] {
    return uniqueBy(this.campaignTracks, 'playlist_spotify_id')
      .map((track: RestTrack) => ({
        id: -1,
        title: track.playlist_name,
        subtitle: '',
        images: track.playlist_images,
        linkUrl: `/playlist/${track.playlist_spotify_id}`
      }))
      .filter((item) => {
        if (this.searchQuery === '' || this.tabsIndexActive !== 2) {
          return true
        } else {
          return item.title.toLowerCase().includes(this.searchQueryLow) ||
            item.subtitle.toLowerCase().includes(this.searchQueryLow)
        }
      })
      .filter((item) => !!item.title)
  }

  onTrackIcon (url: string) {
    this.$router.push(url)
  }

  onTrack (id: number) {
    if (this.trackIdsSelected.includes(id)) {
      this.trackIdsSelected.splice(this.trackIdsSelected.indexOf(id), 1)
    } else {
      if (this.isLimitReached) return

      this.trackIdsSelected.push(id)
    }
  }

  @Mutation('SET_IS_FOOTER_OPPEN') setIsFooterOpen!: (val: boolean) => void
}
</script>
